<template>
  <div>
    <div class="alert-ta-wrap relative w-full h-64 bg-gray-200 rounded-lg">
      <!-- Mirror behind the textarea: transparent text, highlights chars past the limit -->
      <div
        ref="mirror"
        class="alert-ta-shared alert-ta-mirror absolute inset-0"
        aria-hidden="true"
        translate="no"
      >{{ withinLimit }}<mark class="alert-ta-overflow">{{ overLimit }}</mark>{{ "​" }}</div>
      <textarea
        ref="textarea"
        class="alert-ta-shared alert-ta-input relative w-full h-full"
        data-gramm="false"
        :value="modelValue ?? ''"
        :placeholder="placeholder"
        @input="onInput"
        @compositionend="onInput"
        @scroll="syncScroll"
        @blur="emit('blur')"
      />
    </div>
    <p v-if="removedChars.length > 0" class="mt-1 text-sm text-orange-600">
      Removed characters not supported in SMS: {{ removedChars.join(" ") }}
    </p>
    <p v-if="isOverSmsMax" class="mt-1 text-sm text-red-700">
      Over {{ gsmMaxCharsForParts(GSM_MAX_PARTS) }} characters<template v-if="!hideSmsParts">
        ({{ GSM_MAX_PARTS }} SMS parts)</template>: email gets the full message, but SMS may be
      rejected unless a short mobile version is used.
    </p>
    <AlertCharCounter :message="modelValue" :limit="limit" :hide-sms-parts="hideSmsParts" />
  </div>
</template>

<script setup lang="ts">
import {
  GSM_MAX_PARTS,
  gsmCharWeight,
  gsmMaxCharsForParts,
  gsmPartCount,
  toGsm,
} from "../helpers/gsm";

const props = withDefaults(
  defineProps<{
    modelValue: string | null | undefined;
    /** Soft warning limit (highlight + counter turns orange) */
    limit?: number;
    placeholder?: string;
    /** Hide the SMS part count (client-facing pages) */
    hideSmsParts?: boolean;
  }>(),
  {
    limit: gsmMaxCharsForParts(3),
    placeholder: "",
    hideSmsParts: false,
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "blur"): void;
}>();

const mirror = ref<HTMLDivElement | null>(null);
const textarea = ref<HTMLTextAreaElement | null>(null);
const removedChars = ref<string[]>([]);
// Not capped (email gets the full text), but AWS rejects SMS over the max parts
const isOverSmsMax = computed(() => gsmPartCount(props.modelValue) > GSM_MAX_PARTS);

// Index of the first char that pushes the GSM count (extension chars = 2) past the limit
const splitIndex = computed(() => {
  const text = props.modelValue ?? "";
  let count = 0;
  for (let i = 0; i < text.length; i++) {
    count += gsmCharWeight(text[i]);
    if (count > props.limit) return i;
  }
  return text.length;
});
const withinLimit = computed(() => (props.modelValue ?? "").slice(0, splitIndex.value));
const overLimit = computed(() => (props.modelValue ?? "").slice(splitIndex.value));

function onInput(event: Event) {
  // Let IME composition finish before cleaning; compositionend re-runs this
  if ((event as InputEvent).isComposing) return;

  const el = event.target as HTMLTextAreaElement;
  const raw = el.value;
  const { text, removed } = toGsm(raw);
  // Keep the caret where the user was typing after replacements/removals
  const caret = toGsm(raw.slice(0, el.selectionStart ?? raw.length)).text.length;

  if (text !== raw) {
    el.value = text;
    el.setSelectionRange(caret, caret);
  }

  removedChars.value = removed;
  emit("update:modelValue", text);
  nextTick(syncScroll);
}

function syncScroll() {
  if (!mirror.value || !textarea.value) return;
  mirror.value.scrollTop = textarea.value.scrollTop;
  mirror.value.scrollLeft = textarea.value.scrollLeft;
}

// Value can change without input (template selection, reset after send)
watch(
  () => props.modelValue,
  (value) => {
    const { text, removed } = toGsm(value);
    if (value && text !== value) {
      removedChars.value = removed;
      emit("update:modelValue", text);
    }
    nextTick(syncScroll);
  },
  { immediate: true }
);
</script>

<style scoped>
.alert-ta-wrap {
  color-scheme: light;
}

/* Both layers must share identical box + text metrics so wrapping matches */
.alert-ta-shared {
  box-sizing: border-box;
  margin: 0;
  padding: 0.75rem 1rem;
  border: 1px solid transparent;
  border-radius: 0.5rem;
  font: inherit;
  line-height: 1.5;
  letter-spacing: normal;
  word-spacing: normal;
  text-indent: 0;
  text-transform: none;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-break: normal;
  tab-size: 4;
  /* Always reserve the scrollbar gutter so classic (Windows/Linux) scrollbars don't change wrap width */
  overflow-y: scroll;
  overflow-x: hidden;
}

.alert-ta-mirror {
  color: transparent;
  pointer-events: none;
  user-select: none;
  scrollbar-color: transparent transparent;
}

.alert-ta-mirror::-webkit-scrollbar {
  background: transparent;
}

.alert-ta-mirror::-webkit-scrollbar-thumb,
.alert-ta-mirror::-webkit-scrollbar-track {
  background: transparent;
}

.alert-ta-input {
  background: transparent;
  border-color: #e5e7eb;
  resize: none;
  -webkit-appearance: none;
  appearance: none;
  overscroll-behavior: none;
  outline: none;
}

.alert-ta-overflow {
  background-color: #fed7aa;
  color: transparent;
  border-radius: 2px;
  padding: 0;
}
</style>
